import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { parseFrontmatter } from '../utils/frontmatter';
import placeholderImage from '../assets/images/placeholder-avatar.svg';

export interface CaseStudyData {
  name: string;
  breed: string;
  summary: string;
  outcome: string;
  beforeImage: string;
  afterImage: string;
  story: string;
}

const cache = new Map<string, CaseStudyData>();

export const useCaseStudyContent = (slug: string) => {
  const { i18n } = useTranslation();
  const [data, setData] = useState<CaseStudyData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const lang = i18n.language;
    const key = `${lang}/case-studies/${slug}`;

    if (cache.has(key)) {
      setData(cache.get(key)!);
      setLoading(false);
      return;
    }

    setLoading(true);
    fetch(`${process.env.PUBLIC_URL}/content/${lang}/case-studies/${slug}.md`)
      .then((res) => {
        if (!res.ok) throw new Error('Not found');
        return res.text();
      })
      .then((raw) => {
        const { metadata, content } = parseFrontmatter(raw);

        // Derive name from slug: capitalize first letter
        const name = slug.charAt(0).toUpperCase() + slug.slice(1);

        const caseStudyData: CaseStudyData = {
          name,
          breed: metadata.breed || '',
          summary: metadata.summary || '',
          outcome: metadata.outcome || '',
          beforeImage: metadata.beforeImage || placeholderImage,
          afterImage: metadata.afterImage || placeholderImage,
          story: content,
        };

        cache.set(key, caseStudyData);
        setData(caseStudyData);
      })
      .catch(() => {
        setData(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [slug, i18n.language]);

  return { data, loading };
};
